import { useEffect, useRef, useState, useMemo } from "react";
import { Link, RouteComponentProps, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useResource } from "@/hooks/resources";
import MenuPageLayout from "@/components/layouts/MenuPageLayout";
import { request } from "@/utils/request";
import { Tabs, Tab } from "@/components/tabs";
import SongType from "@/types/song-type";
import { Modal } from "flowbite-react";
import { Swiper, SwiperSlide } from "swiper/react";
import VocabularyType from "@/types/vocabulary-type";
import ScoreBalls from "@/components/songs/score-balls";
import Skeleton from "react-loading-skeleton";
import makeYourOwnSongTabImage from "@/assets/make-your-own-song-tab.png";
import { storage } from "@/utils/storage";
import { stageAssetPreloader } from "@/utils/stage-asset-preloader";
import { useAppContext } from "@/contexts/app-context";
import { AudioPlayer } from "@/components/gameplay/audio-player";

interface CreateSongResponse {
  id: number;
  name: string;
}

const SongPage = ({ match }: RouteComponentProps<{ id: string }>) => {
  const songId = match?.params?.id;
  const location = useLocation();

  const { state: { currentUser } } = useAppContext();
  const [song, { isLoading }] = useResource<SongType>(`/songs/${songId}`);

  // Preload first stage assets when song data is loaded
  useEffect(() => {
    if (!song || !song.stages || song.stages.length === 0) return;

    const firstStage = song.stages.find((stage) => stage.number === 1);
    if (firstStage) {
      stageAssetPreloader.preloadStage(firstStage, song).catch(error => {
        console.error('Error preloading first stage assets:', error);
      });
    }
  }, [song]);

  const [playingAudio, setPlayingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [currentVocabulary, setCurrentVocabulary] = useState<VocabularyType>();
  const [makeSongName, setMakeSongName] = useState("");
  
  // Build template lines from song lyrics HTML: replace vocabulary words with placeholders
  const templateLines = useMemo(() => {
    if (!song?.lyricsHtml || !song?.vocabularies?.length) return [];

    // Build regex pattern from vocabulary words
    const vocabularyPattern = song.vocabularies
      .map(v => v.name)
      .filter(Boolean)
      .map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // Escape regex special chars
      .join('|');
    
    if (!vocabularyPattern) return [];
    
    const regex = new RegExp(`\\b(${vocabularyPattern})\\b`, 'gi');
    
    // Normalize breaks to newlines, split into lines, and replace vocabulary words
    return song.lyricsHtml
      .replace(/<br\s*\/?>/gi, "\n")
      .split(/\n+/)
      .map(line => line.replace(/<[^>]+>/g, "").replace(regex, "[choose-word]"))
      .map(line => line.trim())
      .filter(Boolean);
  }, [song?.lyricsHtml, song?.vocabularies]);

  const [chosenWords, setChosenWords] = useState<
    (VocabularyType | undefined)[]
  >(() => Array(templateLines.length).fill(undefined));
  const [selectingLineIndex, setSelectingLineIndex] = useState<number | null>(
    null,
  );
  const myosTopRef = useRef<HTMLDivElement>(null);
  const [currentTab, setCurrentTab] = useState<string>("preview-song");
  const [allWordOptions, setAllWordOptions] = useState<Record<string, Array<{
    id: number;
    word: string;
    img_url: string;
  }>>>({});
  const [loadingWordOptions, setLoadingWordOptions] = useState(false);
  const [hasFetchedWordOptions, setHasFetchedWordOptions] = useState(false);
  const [previewVideoCompleted, setPreviewVideoCompleted] = useState(false);

  // Load preview video completed status from storage
  useEffect(() => {
    const loadPreviewVideoStatus = async () => {
      if (!songId) return;
      const storageKey = `preview_video_completed_${songId}`;
      const completed = await storage.get(storageKey);
      if (completed === true) {
        setPreviewVideoCompleted(true);
      }
    };
    loadPreviewVideoStatus();
  }, [songId]);

  // Handle URL tab parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam) {
      setCurrentTab(tabParam);
      // Mark preview video as completed to show Make Your Own Song tab
      if (tabParam === "make-your-own") {
        setPreviewVideoCompleted(true);
        // Save to storage so it persists
        if (songId) {
          const storageKey = `preview_video_completed_${songId}`;
          storage.set(storageKey, true);
        }
      }
    }
  }, [location.search, songId]);

  // Adjust initial tab based on whether song is custom
  useEffect(() => {
    if (song && song.isCustom && currentTab === "preview-song") {
      setCurrentTab("lyrics");
    }
  }, [song, currentTab]);

  // Fetch word options when switching to Make Your Own Song tab
  useEffect(() => {
    const shouldFetchWordOptions =
      currentTab === "make-your-own" &&
      song &&
      !song.isCustom &&
      song.allowCustomization !== false &&
      song.isPlayComplete &&
      !hasFetchedWordOptions;

    if (shouldFetchWordOptions) {
      fetchAllWordOptions();
    }
  }, [currentTab, song, hasFetchedWordOptions]);

  // Ensure chosenWords length matches templateLines length when lyrics load/change
  useEffect(() => {
    setChosenWords(() => Array(templateLines.length).fill(undefined));
  }, [templateLines.length]);

  useEffect(() => {
    if (!audioRef.current) return;

    if (playingAudio) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  }, [playingAudio]);

  const handleAudioEnded = () => {
    setPlayingAudio(false);
  };

  const handlePreviewVideoEnded = async () => {
    setPreviewVideoCompleted(true);
    // Save to storage so it persists across browser sessions
    if (songId) {
      const storageKey = `preview_video_completed_${songId}`;
      await storage.set(storageKey, true);
    }
  };

  const isReady =
    chosenWords.length > 0 &&
    chosenWords.every((w) => !!w) &&
    makeSongName.trim().length > 0;

  // Fetch word options for all lyrics
  const fetchAllWordOptions = async () => {
    if (!song || !song.lyrics || song.lyrics.length === 0) return;

    setLoadingWordOptions(true);
    try {
      const lyricIds = song.lyrics.map(lyric => lyric.id).join(',');
      const response = await request.get(`/songs/word_options?lyric_ids=${lyricIds}`);
      
      if (response.ok) {
        const data = await response.json();
        setAllWordOptions(data);
      } else {
        console.error("Failed to fetch word options:", response.status);
        setAllWordOptions({});
      }
    } catch (error) {
      console.error("Error fetching word options:", error);
      setAllWordOptions({});
    } finally {
      setLoadingWordOptions(false);
      setHasFetchedWordOptions(true);
    }
  };

  // Save custom song function
  const saveCustomSong = async () => {
    // Verify user is authenticated
    const authToken = await import("@/utils/storage").then(storage => storage.storage.get("authToken"));
    if (!authToken) {
      setSaveError("Please log in to save custom songs");
      return;
    }

    // Verify song allows customization
    if (song && song.allowCustomization === false) {
      setSaveError("This song does not allow customization");
      return;
    }

    // Verify song name is provided
    if (!makeSongName.trim()) {
      setSaveError("Please enter a song name to create your custom song");
      return;
    }

    // Verify all words are selected for each line
    const missingWords = chosenWords.some((word, index) => {
      if (!word) {
        setSaveError(`Please choose a word for line ${index + 1} to complete your custom song`);
        return true;
      }
      return false;
    });

    if (missingWords) {
      return;
    }

    // Verify song data is loaded
    if (!song) {
      setSaveError("Song data not loaded. Please refresh the page and try again");
      return;
    }

    // Verify lyrics data is available
    if (!song.lyrics || song.lyrics.length === 0) {
      setSaveError("Song lyrics not available. Cannot create custom song");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Build lyrics array with chosen words
      const lyrics = templateLines.map((line, index) => {
        const chosenWord = chosenWords[index];
        if (!chosenWord) return null;

        // Find the corresponding lyric line from the original song
        const originalLyric = song.lyrics?.[index];
        if (!originalLyric) return null;

        return {
          id: originalLyric.id,
          number: originalLyric.number || index + 1,
          word: chosenWord.name,
          base_vocabulary_id: chosenWord.id
        };
      }).filter(Boolean);

      // Verify all lyrics are processed correctly
      if (lyrics.length !== templateLines.length) {
        setSaveError("Failed to process all lyrics. Please try again");
        return;
      }

      const requestBody = {
        base_song_id: songId,
        name: makeSongName.trim(),
        lyrics: lyrics
      };

      const response = await request.post<CreateSongResponse>("/songs", requestBody);
      
      if (response.ok && response.data) {
        const newSongId = response.data.id;
        
        // Navigate to the new custom song detail page
        window.location.href = `/songs/${newSongId}`;
      } else {
        // Handle different error responses
        if (response.status === 401) {
          setSaveError("Authentication required. Please log in to save custom songs.");
        } else if (response.status === 403) {
          setSaveError("This song does not allow customization");
        } else {
          setSaveError(`Failed to save custom song: ${response.message || "Unknown error"}`);
        }
      }
    } catch (error) {
      setSaveError("Network error occurred while saving the song");
      console.error("Save custom song error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <MenuPageLayout title={song?.name}>
      {isLoading || !song || selectingLineIndex !== null ? (
        selectingLineIndex !== null ? (
          /* Show word selection interface when choosing word */
          <div className="bg-white rounded-3xl pt-3 px-6 pb-6 flex flex-col h-full shadow-lg">
            {loadingWordOptions ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-lg">Loading word options...</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-4 p-4 flex-1 overflow-y-auto">
                {(() => {
                  if (selectingLineIndex === null || !song?.lyrics) return [];
                  const lyricId = song.lyrics[selectingLineIndex].id.toString();
                  return allWordOptions[lyricId] || [];
                })().map((option) => (
                  <div
                    key={option.id}
                    className="group p-6 border border-solid border-gray-400 rounded-2xl flex flex-col items-center gap-4"
                  >
                    <img
                      src={option.img_url}
                      className="object-contain object-center w-full aspect-square rounded-lg"
                    />

                    <h3 className="text-xl text-center font-semibold">
                      {option.word}
                    </h3>

                    <button
                      onClick={() => {
                        if (selectingLineIndex === null) return;
                        const copy = [...chosenWords];
                        // Create a vocabulary-like object from the API response
                        copy[selectingLineIndex] = {
                          id: option.id,
                          name: option.word,
                          images: [{ url: option.img_url }]
                        } as VocabularyType;
                        setChosenWords(copy);
                        setSelectingLineIndex(null);
                        setCurrentTab("make-your-own");
                        // Scroll to top of MYOS lyrics after word selection
                        setTimeout(() => {
                          myosTopRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      className="btn-primary w-full"
                    >
                      Select
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Loading skeleton */
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            <section className="shrink-0 w-full sm:w-1/5 flex flex-col gap-4">
              <Skeleton
                containerClassName="block w-full aspect-square leading-none"
                className="size-full"
              />

              <Skeleton className="h-12" />
            </section>

            <section className="w-full flex flex-col gap-y-2">
              <div className="flex gap-2">
                <Skeleton className="h-8" containerClassName="w-36" />
                <Skeleton className="h-8" containerClassName="w-20" />
                <Skeleton className="h-8" containerClassName="w-32" />
              </div>

              <Skeleton
                containerClassName="w-full h-96"
                className="size-full"
              />
            </section>
          </div>
        )
      ) : (
        <div className="flex flex-col sm:flex-row gap-4 w-full">
          <section className="shrink-0 w-full sm:w-1/5 flex flex-col gap-4">
            <img
              src={song?.image?.url}
              className="p-2 border shadow-lg aspect-square"
            />

            <Link to={`/songs/${songId}/play`} className="btn-primary">
              PLAY
              <FontAwesomeIcon icon="play" />
            </Link>

            <div className="flex justify-end">
              <ScoreBalls value={song.semanticsScore} type="semantics" />
              <ScoreBalls value={song.articulationScore} type="articulation" />
            </div>
          </section>

          <div className="relative flex-1">
            <Tabs
              current={currentTab}
              onTabChange={(tabValue) => setCurrentTab(tabValue as string)}
            >
              {!song?.isCustom && (
                <Tab label="Preview Song" value="preview-song">
                  <video
                    src={song?.gameplayPreviewVideo?.url}
                    controls
                    className="w-full"
                    onEnded={handlePreviewVideoEnded}
                  />
                </Tab>
              )}

              <Tab label="Lyrics" value="lyrics">
                <div className="flex gap-4 p-4">
                  {!song?.isCustom && (
                    <div>
                      <button
                        className="size-16 ring-2 ring-white text-gray-500 bg-gray-100 hover:bg-gray-300 rounded-full flex items-center justify-center"
                        onClick={() => setPlayingAudio(!playingAudio)}
                      >
                        {playingAudio ? (
                          <FontAwesomeIcon icon="pause" />
                        ) : (
                          <FontAwesomeIcon icon="play" />
                        )}
                      </button>

                      <AudioPlayer
                        src={song?.fullAudio?.url}
                        ref={audioRef}
                        onEnded={handleAudioEnded}
                      />
                    </div>
                  )}

                  <div
                    className="text-2xl leading-loose whitespace-pre-wrap [&>span]:font-semibold"
                    dangerouslySetInnerHTML={{ __html: song?.lyricsHtml || "" }}
                  />
                </div>
              </Tab>

              <Tab label="Vocabulary" value="vocabulary">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                  {(song?.vocabularies || []).map(
                    (vocabulary: VocabularyType) => (
                      <button
                        key={vocabulary.id}
                        className="group p-4 border border-solid border-gray-400 rounded-2xl flex flex-col items-center gap-4"
                        onClick={() => setCurrentVocabulary(vocabulary)}
                      >
                        <img
                          src={vocabulary.images[0]?.url}
                          className="object-contain object-center w-full aspect-square rounded-lg"
                        />

                        <h3 className="text-xl group-hover:underline">
                          {vocabulary.name}
                        </h3>
                      </button>
                    ),
                  )}
                </div>

                <Modal
                  show={!!currentVocabulary}
                  position="center"
                  onClose={() => setCurrentVocabulary(undefined)}
                  popup
                  dismissible
                >
                  <Modal.Header />
                  <Modal.Body>
                    <div className="py-2 flex flex-col items-center gap-4 text-black">
                      <Swiper
                        className="w-full rounded-xl ring-4 ring-gray-400"
                      >
                        {currentVocabulary?.images.map((image) => (
                          <SwiperSlide key={image.url}>
                            <img
                              src={image.url}
                              className="block object-cover object-center w-full aspect-square cursor-grab"
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>

                      <h2 className="text-2xl text-black">
                        {currentVocabulary?.name}
                      </h2>
                    </div>
                  </Modal.Body>
                </Modal>
              </Tab>

              {song?.isCustom && song?.baseSongId && currentUser?.focusGroup !== 'SS4K' && song?.isPlayComplete && (
                <Tab
                  label={
                    <img
                      src={makeYourOwnSongTabImage}
                      alt="Make Your Own Song"
                      className="h-10 w-auto min-w-[80px] md:min-w-0 object-contain -my-3 -mx-3 inline-block align-middle"
                    />
                  }
                  value="make-your-own-base"
                >
                  <div className="p-4 flex flex-col items-center gap-6">
                    <a
                      href={`/songs/${song.baseSongId}?tab=make-your-own`}
                      className="btn btn-primary text-lg px-8 py-3"
                    >
                      Click here to Make Your Own Song
                    </a>
                  </div>
                </Tab>
              )}

              {!song?.isCustom && song?.allowCustomization !== false && currentUser?.focusGroup !== 'SS4K' && song?.isPlayComplete && (
                <Tab 
                  label={
                    <img 
                      src={makeYourOwnSongTabImage} 
                      alt="Make Your Own Song" 
                      className="h-10 w-auto min-w-[80px] md:min-w-0 object-contain -my-3 -mx-3 inline-block align-middle"
                    />
                  } 
                  value="make-your-own"
                >
                <div className="p-4 flex flex-col items-center gap-6">
                  <div className="w-full max-w-2xl mx-auto">
                    <div ref={myosTopRef} className="flex flex-col gap-0 -space-y-1">
                      {templateLines.map((line, idx) => {
                        const parts = line.split("[choose-word]");
                        const chosen = chosenWords[idx];
                        const imgUrl = chosen?.images?.[0]?.url;
                        return (
                          <div
                            key={idx}
                            className="flex items-center justify-between -my-0.5"
                          >
                            <div className="flex-1 text-right text-xl font-semibold leading-none">
                              <span className="mr-2">{parts[0]}</span>
                              <button
                                onClick={() => {
                                  setSelectingLineIndex(idx);
                                }}
                                className="text-sky-400 font-semibold underline"
                              >
                                {chosen?.name || "[choose word]"}
                              </button>
                              <span className="ml-2">{parts[1] || ""}</span>
                            </div>

                            <div className={`${imgUrl ? 'w-24 h-24' : 'w-16 h-16'} ml-4 flex items-center justify-end`}>
                              {imgUrl ? (
                                <img
                                  src={imgUrl}
                                  alt={chosen?.name}
                                  className="w-20 h-20 object-cover rounded-lg"
                                />
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="w-full mx-auto flex flex-col md:flex-row items-center md:items-center gap-4 mt-4">
                        <h2 className="text-3xl font-semibold whitespace-nowrap">Name your song!</h2>
                        <input
                          value={makeSongName}
                          onChange={(e) => setMakeSongName(e.target.value)}
                          placeholder="Type song name..."
                          className="w-full md:flex-1 border-2 border-gray-300 rounded-lg p-3 text-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        />
                      </div>

                    <div className="mt-6 flex flex-col items-center gap-4">
                      {/** Disabled until song name filled and all placeholders are chosen */}
                      {isReady ? (
                        <button
                          onClick={saveCustomSong}
                          disabled={isSaving}
                          className="btn-primary w-full max-w-md mx-auto whitespace-nowrap disabled:opacity-60 disabled:pointer-events-none"
                        >
                          {isSaving ? "Saving..." : "Save and Play Song"}
                        </button>
                      ) : (
                        <button className="btn-primary w-full max-w-md mx-auto whitespace-nowrap opacity-60 pointer-events-none">
                          Choose all words to save and play song
                        </button>
                      )}

                      {saveError && (
                        <div className="text-red-500 text-center max-w-80">
                          {saveError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Tab>
              )}
            </Tabs>
          </div>
        </div>
      )}
    </MenuPageLayout>
  );
};

export default SongPage;
